export type ProfileSectionId =
  | 'actividad'
  | 'clubes'
  | 'logros'
  | 'guardados'
  | 'eventos'
  | 'concursos'
  | 'seguidores'
  | 'siguiendo';

export type ListenerStatusId =
  | 'top-fan'
  | 'vip'
  | 'embajador'
  | 'ganador'
  | 'campeon';

export interface ListenerStatus {
  id: ListenerStatusId;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
  ringColor: string;
  description: string;
  earnedDate: string;
  level: string;
}

export interface ListenerBadge {
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  description: string;
}

export interface ListenerLevel {
  name: string;
  number: number;
  progress: number;
  nextLevel: string;
  pointsToNext: number;
  totalPoints: number;
}

export interface ProfileSection {
  id: ProfileSectionId;
  name: string;
  icon: string;
  description: string;
  count: number;
  color: string;
  bgColor: string;
  link: string;
}

export interface ActivityItem {
  id: number;
  type: string;
  description: string;
  icon: string;
  color: string;
  timestamp: string;
  link: string;
}

export interface ClubItem {
  id: number;
  name: string;
  role: string;
  members: number;
  icon: string;
  color: string;
  joinedDate: string;
}

export interface AchievementItem {
  id: number;
  name: string;
  description: string;
  icon: string;
  tier: string;
  tierColor: string;
  earnedDate: string;
}

export interface SavedItem {
  id: number;
  title: string;
  type: string;
  typeIcon: string;
  savedDate: string;
  isFavorite: boolean;
}

export interface EventItem {
  id: number;
  title: string;
  date: string;
  type: string;
  icon: string;
  status: string;
}

export interface ContestItem {
  id: number;
  title: string;
  radio: string;
  result: string;
  resultColor: string;
  icon: string;
  date: string;
}

export interface FollowerItem {
  id: number;
  name: string;
  username: string;
  photo: string;
  isMutual: boolean;
  badges: string[];
}

export interface FollowingItem {
  id: number;
  name: string;
  username: string;
  photo: string;
  radioName: string;
  isLive: boolean;
  type: 'oyente' | 'radio' | 'locutor';
}

export interface ProfileStats {
  totalPoints: number;
  ranking: number;
  followers: number;
  following: number;
  clubesCount: number;
  logrosCount: number;
  guardadosCount: number;
  eventosCount: number;
  concursosGanados: number;
  diasActivo: number;
  rachaDias: number;
}

export interface ListenerSocialProfile {
  id: number;
  coverUrl: string;
  photoUrl: string;
  name: string;
  username: string;
  city: string;
  country: string;
  countryFlag: string;
  joinDate: string;
  bio: string;
  level: ListenerLevel;
  badges: ListenerBadge[];
  stats: ProfileStats;
  statuses: ListenerStatus[];
  sections: ProfileSection[];
  actividad: ActivityItem[];
  clubes: ClubItem[];
  logros: AchievementItem[];
  guardados: SavedItem[];
  eventos: EventItem[];
  concursos: ContestItem[];
  followersList: FollowerItem[];
  followingList: FollowingItem[];
}

export const listenerStatuses: ListenerStatus[] = [
  {
    id: 'top-fan',
    name: 'Top Fan',
    icon: 'ri-fire-line',
    color: 'bg-orange-500',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    ringColor: 'ring-orange-300',
    description: 'Reconocido como uno de los fans más activos de la comunidad RFW, con interacciones diarias y compromiso excepcional.',
    earnedDate: '2025-09-15',
    level: 'Diamante',
  },
  {
    id: 'vip',
    name: 'VIP',
    icon: 'ri-vip-crown-line',
    color: 'bg-amber-500',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    ringColor: 'ring-amber-300',
    description: 'Miembro exclusivo del Club VIP RFW con acceso a contenido premium, eventos privados y beneficios únicos.',
    earnedDate: '2025-06-01',
    level: 'Oro',
  },
  {
    id: 'embajador',
    name: 'Embajador',
    icon: 'ri-shield-star-line',
    color: 'bg-indigo-500',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-700',
    ringColor: 'ring-indigo-300',
    description: 'Embajador oficial RFW que representa los valores de la plataforma y promueve activamente la comunidad radial.',
    earnedDate: '2026-01-20',
    level: 'Platino',
  },
  {
    id: 'ganador',
    name: 'Ganador',
    icon: 'ri-trophy-line',
    color: 'bg-yellow-500',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    ringColor: 'ring-yellow-300',
    description: 'Ha ganado 3 concursos en la plataforma, demostrando talento, dedicación y conexión con la comunidad.',
    earnedDate: '2025-11-10',
    level: 'Triple',
  },
  {
    id: 'campeon',
    name: 'Campeón',
    icon: 'ri-medal-line',
    color: 'bg-rose-500',
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-700',
    ringColor: 'ring-rose-300',
    description: 'Campeón de la Copa RFW en la categoría Comunidad, el reconocimiento más alto a la excelencia como oyente.',
    earnedDate: '2026-03-15',
    level: 'Oro',
  },
];

export const profileSections: ProfileSection[] = [
  { id: 'actividad', name: 'Actividad', icon: 'ri-flashlight-line', description: 'Lo último que hiciste en la plataforma', count: 342, color: 'text-sky-600', bgColor: 'bg-sky-50', link: '/historial' },
  { id: 'clubes', name: 'Clubes', icon: 'ri-group-line', description: 'Los clubes de fans donde participás', count: 5, color: 'text-violet-600', bgColor: 'bg-violet-50', link: '/clubes' },
  { id: 'logros', name: 'Logros', icon: 'ri-medal-2-line', description: 'Tus logros y reconocimientos', count: 12, color: 'text-amber-600', bgColor: 'bg-amber-50', link: '/logros' },
  { id: 'guardados', name: 'Guardados', icon: 'ri-bookmark-line', description: 'Contenido que guardaste para ver después', count: 35, color: 'text-teal-600', bgColor: 'bg-teal-50', link: '/guardados' },
  { id: 'eventos', name: 'Eventos', icon: 'ri-calendar-event-line', description: 'Eventos a los que asistís o asistirás', count: 7, color: 'text-rose-600', bgColor: 'bg-rose-50', link: '/eventos' },
  { id: 'concursos', name: 'Concursos', icon: 'ri-trophy-line', description: 'Concursos en los que participaste', count: 8, color: 'text-orange-600', bgColor: 'bg-orange-50', link: '/ganadores' },
  { id: 'seguidores', name: 'Seguidores', icon: 'ri-user-heart-line', description: 'Personas que te siguen', count: 1247, color: 'text-pink-600', bgColor: 'bg-pink-50', link: '/seguidores' },
  { id: 'siguiendo', name: 'Siguiendo', icon: 'ri-user-star-line', description: 'Radios, locutores y oyentes que seguís', count: 28, color: 'text-emerald-600', bgColor: 'bg-emerald-50', link: '/seguidores' },
];

export const listenerProfile: ListenerSocialProfile = {
  id: 101,
  coverUrl: 'https://readdy.ai/api/search-image?query=Warm%20gradient%20abstract%20background%20with%20soft%20overlapping%20circles%20and%20flowing%20organic%20shapes%20in%20coral%20pink%20amber%20gold%20and%20warm%20cream%20tones%20elegant%20modern%20sophisticated%20social%20media%20profile%20cover%20banner%20style%20with%20subtle%20depth%20and%20gentle%20glow%20effects%20artistic%20minimalist%20composition&width=1600&height=500&seq=lsp-cover-marco-01&orientation=landscape',
  photoUrl: 'https://readdy.ai/api/search-image?query=Young%20confident%20Latino%20male%20portrait%20with%20warm%20genuine%20friendly%20smile%20wearing%20casual%20olive%20green%20henley%20shirt%20clean%20soft%20warm%20gradient%20cream%20background%20with%20gentle%20natural%20lighting%20approachable%20authentic%20headshot%20editorial%20portrait%20style%20shallow%20depth%20of%20field&width=400&height=400&seq=lsp-avatar-marco-01&orientation=squarish',
  name: 'Marco Antonio Rivera',
  username: '@marco_oyente',
  city: 'Ciudad de México',
  country: 'México',
  countryFlag: '🇲🇽',
  joinDate: '2020-03-15',
  bio: 'Amante de la música regional mexicana, el rock clásico y la buena radio. Oyente fiel desde 2020. Coleccionista de playeras de estaciones de radio y fan de los maratones musicales de fin de semana. Siempre con la radio encendida mientras trabajo. Mi cabina es mi segunda casa.',
  level: {
    name: 'Top Fan',
    number: 4,
    progress: 67,
    nextLevel: 'VIP',
    pointsToNext: 1150,
    totalPoints: 4850,
  },
  badges: [
    { name: 'Oyente Fiel', icon: 'ri-heart-pulse-line', color: 'text-rose-600', bgColor: 'bg-rose-50', description: 'Más de 1000 horas de escucha acumuladas' },
    { name: 'Madrugador', icon: 'ri-sun-line', color: 'text-amber-600', bgColor: 'bg-amber-50', description: '50 transmisiones sintonizadas antes de las 7 AM' },
    { name: 'Mega Fan', icon: 'ri-star-smile-line', color: 'text-orange-600', bgColor: 'bg-orange-50', description: 'Sigue más de 10 radios activamente' },
    { name: 'Comunicador', icon: 'ri-chat-smile-2-line', color: 'text-emerald-600', bgColor: 'bg-emerald-50', description: 'Más de 300 mensajes enviados al aire' },
    { name: 'Leyenda', icon: 'ri-vip-diamond-line', color: 'text-purple-600', bgColor: 'bg-violet-50', description: 'Miembro activo por más de 5 años consecutivos' },
  ],
  stats: {
    totalPoints: 4850,
    ranking: 42,
    followers: 1247,
    following: 28,
    clubesCount: 5,
    logrosCount: 12,
    guardadosCount: 35,
    eventosCount: 7,
    concursosGanados: 3,
    diasActivo: 1825,
    rachaDias: 34,
  },
  statuses: listenerStatuses,
  sections: profileSections,
  actividad: [
    { id: 1, type: 'escucho', description: 'Escuchó El Sabor del Mediodía en La Mexicana FM por 90 minutos', icon: 'ri-headphone-line', color: 'bg-sky-50 text-sky-600', timestamp: 'Hace 10 minutos', link: '/radio/la-mexicana-fm' },
    { id: 2, type: 'mensaje', description: 'Envió un saludo al aire en La Mexicana FM — leído por Alejandro Ramírez', icon: 'ri-message-2-line', color: 'bg-emerald-50 text-emerald-600', timestamp: 'Hace 45 minutos', link: '/radio/la-mexicana-fm' },
    { id: 3, type: 'reaccion', description: 'Reaccionó con 🔥 Está Brutal al post de Radio Élite Digital', icon: 'ri-fire-line', color: 'bg-orange-50 text-orange-600', timestamp: 'Hace 2 horas', link: '/radio/radio-elite-digital' },
    { id: 4, type: 'guardado', description: 'Guardó el video Tutorial de Jingles Profesionales de Radio Peninsular', icon: 'ri-bookmark-line', color: 'bg-teal-50 text-teal-600', timestamp: 'Hace 3 horas', link: '/guardados' },
    { id: 5, type: 'concurso', description: 'Se inscribió en el concurso Crea tu Jingles 2026 de Radio Peninsular', icon: 'ri-trophy-line', color: 'bg-amber-50 text-amber-600', timestamp: 'Hace 5 horas', link: '/concursos' },
    { id: 6, type: 'evento', description: 'Confirmó asistencia al RadioFest 2026 — 3 días de música en vivo', icon: 'ri-calendar-check-line', color: 'bg-rose-50 text-rose-600', timestamp: 'Ayer', link: '/eventos' },
    { id: 7, type: 'insignia', description: 'Obtuvo la insignia Leyenda por 5 años de fidelidad continua', icon: 'ri-vip-diamond-line', color: 'bg-violet-50 text-violet-600', timestamp: 'Ayer', link: '/insignias' },
  ],
  clubes: [
    { id: 1, name: 'Club de Fans La Mexicana FM', role: 'Miembro Fundador', members: 12450, icon: 'ri-group-line', color: 'text-orange-600', joinedDate: '2021-06-10' },
    { id: 2, name: 'Rockeros Unidos', role: 'Moderador', members: 8230, icon: 'ri-flashlight-line', color: 'text-red-600', joinedDate: '2022-03-22' },
    { id: 3, name: 'Deportistas al Aire', role: 'Top Contribuidor', members: 5670, icon: 'ri-basketball-line', color: 'text-blue-600', joinedDate: '2023-01-15' },
    { id: 4, name: 'Legión Pop RFW', role: 'Miembro Activo', members: 15100, icon: 'ri-music-line', color: 'text-pink-600', joinedDate: '2024-05-08' },
    { id: 5, name: 'Embajadores RFW Latam', role: 'Líder Regional', members: 3200, icon: 'ri-shield-star-line', color: 'text-indigo-600', joinedDate: '2025-09-30' },
  ],
  logros: [
    { id: 1, name: 'Oyente Premium', description: 'Alcanzó 1000 horas de escucha', icon: 'ri-headphone-line', tier: 'Oro', tierColor: 'text-amber-600', earnedDate: '2021-08-12' },
    { id: 2, name: 'Comunicador Estrella', description: '500 mensajes enviados al aire', icon: 'ri-message-3-line', tier: 'Oro', tierColor: 'text-amber-600', earnedDate: '2022-04-05' },
    { id: 3, name: 'Super Fan', description: 'Sigue más de 10 radios activamente', icon: 'ri-star-smile-line', tier: 'Plata', tierColor: 'text-gray-500', earnedDate: '2022-11-20' },
    { id: 4, name: 'Madrugador Experto', description: '100 transmisiones antes de las 7 AM', icon: 'ri-sun-line', tier: 'Platino', tierColor: 'text-indigo-500', earnedDate: '2023-06-15' },
    { id: 5, name: 'Conquistador de Clubes', description: 'Miembro activo en 5 clubes de fans', icon: 'ri-group-2-line', tier: 'Oro', tierColor: 'text-amber-600', earnedDate: '2024-01-28' },
    { id: 6, name: 'Ganador Serial', description: 'Ganó 3 concursos en la plataforma', icon: 'ri-trophy-line', tier: 'Platino', tierColor: 'text-indigo-500', earnedDate: '2024-09-10' },
    { id: 7, name: 'Embajador RFW', description: 'Se convirtió en Embajador Oficial', icon: 'ri-shield-star-line', tier: 'Diamante', tierColor: 'text-sky-500', earnedDate: '2025-01-20' },
    { id: 8, name: 'VIP RFW', description: 'Accedió al Club VIP Exclusivo', icon: 'ri-vip-crown-line', tier: 'Diamante', tierColor: 'text-sky-500', earnedDate: '2025-06-01' },
    { id: 9, name: 'Top Fan Global', description: 'Top 50 del ranking global de fans', icon: 'ri-fire-line', tier: 'Oro', tierColor: 'text-amber-600', earnedDate: '2025-09-15' },
    { id: 10, name: 'Campeón de Copa', description: 'Campeón Copa RFW categoría Comunidad', icon: 'ri-medal-line', tier: 'Diamante', tierColor: 'text-sky-500', earnedDate: '2026-03-15' },
    { id: 11, name: 'Racha de Fuego', description: '30 días seguidos de actividad', icon: 'ri-flashlight-line', tier: 'Plata', tierColor: 'text-gray-500', earnedDate: '2026-05-10' },
    { id: 12, name: 'Leyenda RFW', description: '5 años de fidelidad continua', icon: 'ri-vip-diamond-line', tier: 'Diamante', tierColor: 'text-sky-500', earnedDate: '2026-06-15' },
  ],
  guardados: [
    { id: 1, title: 'Guía definitiva para armar tu primer programa de radio', type: 'Post', typeIcon: 'ri-article-line', savedDate: 'Hace 2 horas', isFavorite: true },
    { id: 2, title: 'El backstage completo de la final de Copas RFW', type: 'Video', typeIcon: 'ri-video-line', savedDate: 'Hace 5 horas', isFavorite: true },
    { id: 3, title: 'RadioFest 2026 — El festival de radios más grande', type: 'Evento', typeIcon: 'ri-calendar-event-line', savedDate: 'Ayer', isFavorite: true },
    { id: 4, title: 'El Despertador — Edición Especial 500 episodios', type: 'Programa', typeIcon: 'ri-broadcast-line', savedDate: 'Hace 3 días', isFavorite: false },
    { id: 5, title: 'Cómo los clubes de fans están transformando la lealtad', type: 'Post', typeIcon: 'ri-article-line', savedDate: 'Hace 5 días', isFavorite: false },
  ],
  eventos: [
    { id: 1, title: 'RadioFest 2026', date: '15-17 Julio 2026', type: 'Festival', icon: 'ri-music-2-line', status: 'upcoming' },
    { id: 2, title: 'Copa RFW de Locución 2026 — Final', date: '28 Julio 2026', type: 'Competencia', icon: 'ri-trophy-line', status: 'upcoming' },
    { id: 3, title: 'Noche de Locutores — Homenaje', date: '15 Agosto 2026', type: 'Gala', icon: 'ri-star-line', status: 'upcoming' },
    { id: 4, title: 'Encuentro de Radios Comunitarias', date: '5 Septiembre 2026', type: 'Networking', icon: 'ri-group-line', status: 'upcoming' },
    { id: 5, title: 'Fiesta de Aniversario La Mexicana FM', date: '28 Junio 2026', type: 'Especial', icon: 'ri-cake-line', status: 'upcoming' },
    { id: 6, title: 'Summer Fest 2026 en Vivo', date: '10 Agosto 2026', type: 'Festival', icon: 'ri-sun-line', status: 'upcoming' },
    { id: 7, title: 'Noche de Oración y Alabanza', date: '20 Mayo 2026', type: 'Especial', icon: 'ri-heart-line', status: 'past' },
  ],
  concursos: [
    { id: 1, title: 'Copa RFW de Locución 2026', radio: 'RFW Oficial', result: 'En Progreso', resultColor: 'bg-sky-100 text-sky-700', icon: 'ri-trophy-line', date: 'Julio 2026' },
    { id: 2, title: 'Crea tu Jingles 2026', radio: 'Radio Peninsular', result: 'Inscrito', resultColor: 'bg-emerald-100 text-emerald-700', icon: 'ri-music-line', date: 'Junio 2026' },
    { id: 3, title: 'Mejor Podcaster RFW 2026', radio: 'RFW Oficial', result: 'Nominado', resultColor: 'bg-amber-100 text-amber-700', icon: 'ri-mic-line', date: 'Mayo 2026' },
    { id: 4, title: 'La Voz de tu Ciudad', radio: 'Radio Juvenil FM', result: 'Finalista', resultColor: 'bg-violet-100 text-violet-700', icon: 'ri-voiceprint-line', date: 'Marzo 2026' },
    { id: 5, title: 'Concurso Semanal de Mensajes', radio: 'Radio Nueva Actitud', result: 'Ganador', resultColor: 'bg-yellow-100 text-yellow-700', icon: 'ri-message-2-line', date: 'Enero 2026' },
    { id: 6, title: 'Reto 30 Días de Contenido', radio: 'RFW Oficial', result: 'Completado', resultColor: 'bg-emerald-100 text-emerald-700', icon: 'ri-calendar-check-line', date: 'Diciembre 2025' },
    { id: 7, title: 'Mejor Fan del Año', radio: 'La Mexicana FM', result: 'Ganador', resultColor: 'bg-yellow-100 text-yellow-700', icon: 'ri-heart-pulse-line', date: 'Noviembre 2025' },
    { id: 8, title: 'Trivia de Rock Total', radio: 'Rock Total Radio', result: 'Ganador', resultColor: 'bg-yellow-100 text-yellow-700', icon: 'ri-question-answer-line', date: 'Octubre 2025' },
  ],
  followersList: [
    { id: 1, name: 'Sofía Hernández', username: '@sofia_oyente', photo: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20portrait%20friendly%20warm%20smile%20casual%20modern%20style%20soft%20warm%20neutral%20background%20editorial%20headshot%20clean%20professional%20photography&width=120&height=120&seq=lsp-follower-01&orientation=squarish', isMutual: true, badges: ['Top Fan', 'VIP'] },
    { id: 2, name: 'Alejandro Ramírez', username: '@ale_locutor', photo: 'https://readdy.ai/api/search-image?query=Friendly%20Hispanic%20male%20radio%20host%20portrait%20confident%20warm%20smile%20wearing%20denim%20jacket%20over%20white%20shirt%20clean%20warm%20studio%20lighting%20professional%20broadcaster%20headshot&width=120&height=120&seq=lsp-follower-02&orientation=squarish', isMutual: true, badges: ['Locutor', 'Verificado'] },
    { id: 3, name: 'Diego El Lobo Vargas', username: '@ellobo_rock', photo: 'https://readdy.ai/api/search-image?query=Rock%20radio%20host%20male%20portrait%20long%20dark%20hair%20black%20leather%20jacket%20confident%20expression%20dark%20moody%20studio%20lighting%20dramatic%20headshot&width=120&height=120&seq=lsp-follower-03&orientation=squarish', isMutual: false, badges: ['Locutor', 'Embajador'] },
    { id: 4, name: 'Valentina Suárez', username: '@vale_radio', photo: 'https://readdy.ai/api/search-image?query=Young%20Latina%20female%20radio%20host%20portrait%20bright%20genuine%20smile%20casual%20pink%20blouse%20warm%20natural%20lighting%20friendly%20approachable%20headshot%20editorial%20style&width=120&height=120&seq=lsp-follower-04&orientation=squarish', isMutual: true, badges: ['Locutor', 'Ganador'] },
    { id: 5, name: 'Carlos Mendoza', username: '@carlos_fan', photo: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20mid%20twenties%20casual%20style%20warm%20smile%20premium%20portrait%20photography%20soft%20neutral%20background%20editorial%20quality%20headshot&width=120&height=120&seq=lsp-follower-05&orientation=squarish', isMutual: true, badges: ['Top Fan'] },
  ],
  followingList: [
    { id: 1, name: 'La Mexicana FM', username: '@lamexicana', photo: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20guitar%20and%20sombrero%20elements%20circular%20design%20warm%20orange%20and%20green%20colors%20clean%20modern%20broadcast%20emblem%20festive&width=120&height=120&seq=lsp-follow-01&orientation=squarish', radioName: '103.50 FM', isLive: true, type: 'radio' },
    { id: 2, name: 'Rock Total Radio', username: '@rocktotal', photo: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20electric%20guitar%20lightning%20bolt%20circular%20design%20black%20and%20red%20color%20scheme%20modern%20rock%20broadcast%20emblem%20energetic%20bold&width=120&height=120&seq=lsp-follow-02&orientation=squarish', radioName: '87.01 FM', isLive: true, type: 'radio' },
    { id: 3, name: 'Alejandro Ramírez', username: '@ale_locutor', photo: 'https://readdy.ai/api/search-image?query=Friendly%20Hispanic%20male%20radio%20host%20portrait%20confident%20smile%20denim%20jacket%20white%20shirt%20clean%20warm%20lighting%20professional%20headshot&width=120&height=120&seq=lsp-follow-03&orientation=squarish', radioName: 'La Mexicana FM', isLive: true, type: 'locutor' },
    { id: 4, name: 'Valentina Suárez', username: '@vale_radio', photo: 'https://readdy.ai/api/search-image?query=Young%20Latina%20female%20radio%20host%20bright%20smile%20casual%20pink%20blouse%20warm%20lighting%20friendly%20headshot%20editorial%20style&width=120&height=120&seq=lsp-follow-04&orientation=squarish', radioName: 'Radio Nueva Actitud', isLive: false, type: 'locutor' },
    { id: 5, name: 'Deportes Total Radio', username: '@deportestotal', photo: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20soccer%20ball%20trophy%20icon%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%20dynamic%20broadcast%20emblem&width=120&height=120&seq=lsp-follow-05&orientation=squarish', radioName: '120.00 FM', isLive: true, type: 'radio' },
    { id: 6, name: 'Sofía Hernández', username: '@sofia_oyente', photo: 'https://readdy.ai/api/search-image?query=Young%20Latina%20woman%20friendly%20warm%20smile%20casual%20modern%20style%20soft%20warm%20neutral%20background%20editorial%20headshot%20clean%20professional&width=120&height=120&seq=lsp-follow-06&orientation=squarish', radioName: '', isLive: false, type: 'oyente' },
  ],
};